"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Calculator, Gem, Crown, Heart, BookOpen, Telescope, Compass } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function ArabicPartsPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-emerald-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-emerald-500">/</span>
                <span className="text-sm">Arap Parçaları</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Arap Parçaları</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-900/20 to-teal-900/20 rounded-xl p-8 mb-8 border border-emerald-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Gem className="h-10 w-10 text-emerald-400" />
                Arap Parçaları (Arabic Parts)
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Antik dönemden günümüze kadar süren matematiksel formüllerle hesaplanan
                hassas noktalar sistemi. İki veya üç gezegen arasındaki ilişkiyi temel alan
                bu yöntem, yaşamın belirli alanlarında önemli ipuçları ve zamanlama bilgileri sağlar.
                Part of Fortune en bilinen örneğidir.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavram</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-emerald-400" />
                      Hesaplama Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Arabic Parts üç gezegen arasındaki harmoni ve dengeyi
                        matematiksel formüllerle ifade eder: A + B - C
                      </p>
                      <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-emerald-200 mb-2">Temel Formül:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Part = Gezegen A + Gezegen B - Gezegen C</div>
                          <div>Örnek: Fortune = ASC + Ay - Güneş</div>
                          <div>Gündüz/Gece doğum farklılıkları</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Compass className="h-5 w-5 text-teal-400" />
                      Gündüz/Gece Farkı
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Çoğu Arabic Part gündüz ve gece doğumlarda
                        farklı formüller kullanır.
                      </p>
                      <div className="bg-teal-900/20 border border-teal-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-teal-200 mb-2">Part of Fortune:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div><strong>Gündüz:</strong> ASC + Ay - Güneş</div>
                          <div><strong>Gece:</strong> ASC + Güneş - Ay</div>
                          <div>Güneş ASC üzerindeyse gündüz doğum</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Background */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Antik Dönem (MÖ 2. yy - MS 8. yy)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Yunan astroloji geleneğinde başlayan sistem, İslam astronomları
                      tarafından büyük ölçüde geliştirildi ve çeşitlendirildi.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Ptolemy:</strong> İlk sistemli açıklama (Part of Fortune)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Al-Kindi (9. yy):</strong> İslam astronomisinde gelişim</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Al-Biruni (11. yy):</strong> 97 farklı part formülü</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Dönem (20-21. yy)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Robert Hand, Robert Zoller ve Martin Gansten gibi astrologlar
                      tarafından yeniden canlandırıldı ve sistematik hale getirildi.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Robert Hand:</strong> Modern araştırma ve uygulama</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Robert Zoller:</strong> Geleneksel yorumlama teknikleri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Modern yazılımlar:</strong> Otomatik hesaplama</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Arabic Parts */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Başlıca Arap Parçaları</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Part of Fortune */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">⊕</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Fortune (Pars Fortunae)</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
                      <div className="font-medium text-yellow-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div><strong>Gündüz:</strong> ASC + Ay - Güneş</div>
                        <div><strong>Gece:</strong> ASC + Güneş - Ay</div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Anlamı:</div>
                      <div className="text-slate-400">• Materyal refah ve bolluk</div>
                      <div className="text-slate-400">• Genel talih ve şans</div>
                      <div className="text-slate-400">• Fiziksel sağlık ve yaşam gücü</div>
                      <div className="text-slate-400">• Başarı potansiyeli</div>
                    </div>
                  </div>
                </div>

                {/* Part of Spirit */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Spirit (Pars Daimonii)</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div><strong>Gündüz:</strong> ASC + Güneş - Ay</div>
                        <div><strong>Gece:</strong> ASC + Ay - Güneş</div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Anlamı:</div>
                      <div className="text-slate-400">• Ruhsal gelişim ve bilinç</div>
                      <div className="text-slate-400">• Yaratıcı ifade</div>
                      <div className="text-slate-400">• Spiritual rehberlik</div>
                      <div className="text-slate-400">• İç motivasyon kaynakları</div>
                    </div>
                  </div>
                </div>

                {/* Part of Love */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <Heart className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Love</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-pink-900/20 border border-pink-700/50 rounded-lg p-4">
                      <div className="font-medium text-pink-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300">
                        ASC + Venüs - Güneş (erkekler için)<br />
                        ASC + Venüs - Ay (kadınlar için)
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Anlamı:</div>
                      <div className="text-slate-400">• Romantik ilişkiler</div>
                      <div className="text-slate-400">• Aşk deneyimleri</div>
                      <div className="text-slate-400">• Duygusal tatmin</div>
                      <div className="text-slate-400">• Partner seçimi</div>
                    </div>
                  </div>
                </div>

                {/* Part of Marriage */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">∞</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Marriage</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                      <div className="font-medium text-purple-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300">
                        ASC + DSC - Venüs (genel)<br />
                        ASC + 7. ev - Venüs (alternatif)
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Anlamı:</div>
                      <div className="text-slate-400">• Evlilik zamanlaması</div>
                      <div className="text-slate-400">• Partner özellikleri</div>
                      <div className="text-slate-400">• Uzun vadeli ilişkiler</div>
                      <div className="text-slate-400">• Nikah ve bağlılık</div>
                    </div>
                  </div>
                </div>

                {/* Part of Profession */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <Crown className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Profession</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
                      <div className="font-medium text-green-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300">
                        ASC + MC - Güneş (genel)<br />
                        ASC + Satürn - Merkür (alternatif)
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Anlamı:</div>
                      <div className="text-slate-400">• Kariyer yönelimi</div>
                      <div className="text-slate-400">• Mesleki başarı</div>
                      <div className="text-slate-400">• İş fırsatları</div>
                      <div className="text-slate-400">• Toplumsal statü</div>
                    </div>
                  </div>
                </div>

                {/* Part of Death */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-slate-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">⚱</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Part of Death</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-gray-900/20 border border-gray-700/50 rounded-lg p-4">
                      <div className="font-medium text-gray-200 mb-2">Formül:</div>
                      <div className="text-sm text-slate-300">
                        ASC + 8. ev cusp - Ay<br />
                        (Daha az kullanılır, dikkatli yaklaşılmalı)
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300 font-medium">Modern Yorumlama:</div>
                      <div className="text-slate-400">• Dönüşüm süreçleri</div>
                      <div className="text-slate-400">• Yaşam döngüleri</div>
                      <div className="text-slate-400">• Kriz ve yeniden doğuş</div>
                      <div className="text-slate-400">• Psikolojik değişim</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Methods */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Uygulama Yöntemleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-cyan-400" />
                    Temel Analiz Teknikleri
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2">Analiz Adımları:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>1. Part'ın house pozisyonunu belirle</div>
                        <div>2. Part'ın sign (burç) özelliklerini incele</div>
                        <div>3. Part ruler'ını (yöneticisini) analiz et</div>
                        <div>4. Part'a yapılan aspectleri değerlendir</div>
                        <div>5. Transit ve progressionları kontrol et</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Compass className="h-5 w-5 text-purple-400" />
                    Timing ve Aktivasyon
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Transit aktivasyonu:</strong> Gezegenlerin part üzerinden geçişi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Progression aktivasyonu:</strong> Progressed gezegenlerle aspectler</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Solar Arc aktivasyonu:</strong> SA gezegenlerin part'a aspecti</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Return aktivasyonu:</strong> Solar/Lunar return'lerde part pozisyonu</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interpretation Examples */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Örnekleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-yellow-300">Part of Fortune 2. House'da</h3>
                  <div className="space-y-3">
                    <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                      <div className="font-medium text-yellow-200 mb-2">Genel Özellikler:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Materyal zenginlik potansiyeli</div>
                        <div>• Kendi çabalarıyla kazanç</div>
                        <div>• Finansal istikrar arayışı</div>
                        <div>• Değerli eşya ve mülk sevgisi</div>
                      </div>
                    </div>
                    <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4">
                      <div className="font-medium text-green-200 mb-2">Aktivasyon:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Jüpiter transiti: Bolluk dönemi</div>
                        <div>• Venüs transiti: Güzellik ve sanat yatırımları</div>
                        <div>• Satürn transiti: Finansal disiplin</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-pink-300">Part of Love Venüs Conjunction</h3>
                  <div className="space-y-3">
                    <div className="bg-pink-900/20 border border-pink-700/30 rounded-lg p-4">
                      <div className="font-medium text-pink-200 mb-2">Natal Aspect:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Güçlü romantik cazibe</div>
                        <div>• Aşk konularında şanslı</div>
                        <div>• Estetik ve sanat sevgisi</div>
                        <div>• Harmonik ilişki potansiyeli</div>
                      </div>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Transit Aktivasyonu:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Jüpiter transiti: Aşk ve evlilik</div>
                        <div>• Mars transiti: Tutkulu ilişki</div>
                        <div>• Satürn transiti: Ciddi bağlantılar</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Techniques */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">İleri Teknikler</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-indigo-300">Derived Parts</h3>
                    <div className="space-y-3">
                      <p className="text-slate-300 text-sm">
                        Bir part'tan başka part'lar türetme yöntemi.
                        Daha spesifik ve detayı analiz için kullanılır.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Part of Fortune'dan Sağlık Part'ı</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Part of Marriage'dan Çocuk Part'ı</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Part of Profession'dan Başarı Part'ı</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-orange-300">Part Progression</h3>
                    <div className="space-y-3">
                      <p className="text-slate-300 text-sm">
                        Arabic Parts'ların da progression'ları hesaplanabilir.
                        Yaşam boyunca konumlarının değişimi izlenir.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Progressed Part of Fortune</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Solar Arc Parts</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Secondary Progression Parts</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Klasik Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Arabic Parts in Astrology" - Robert Hand</div>
                    <div>• "The Arabic Parts" - Robert Zoller</div>
                    <div>• "Al-Biruni's Book of Instructions" - Al-Biruni</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Lots of Hermes" - Martin Gansten</div>
                    <div>• "Ancient Astrology" - Demetra George</div>
                    <div>• "Traditional Astrology" - John Frawley</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Arabic Parts astrolojinin en karmaşık matematiksel hesaplama tekniklerinden birisidir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel Arabic Parts analizi için deneyimli traditional astrology uzmanlarıyla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/fixed-stars"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Sabit Yıldızlar
              </Link>

              <Link
                href="/about/astrology/advanced-systems/statistical"
                className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              >
                İstatistiksel Astroloji
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}